/**
 * The object being built, rendered beside the builder chat.
 *
 * ## It is the record surface, not a preview of one
 *
 * This is a HOST of `RecordSurfaceProvider` — the same provider and the same
 * header/body/footer regions the record slideout (`SingleSlideout`) and the
 * action center's inline record panel (`InlineCrmObjectPanel`) render. That
 * is deliberate and load-bearing: an earlier version hand-rolled its own
 * field list, then its own projection and provider mounts, and each drifted
 * from the record it was supposed to represent. Anything that improves the
 * record view now improves this automatically, and anything that breaks one
 * breaks all three visibly rather than silently.
 *
 * ## What makes it a draft
 *
 * The agent's pending proposal arrives as preview overlays — derived by
 * `useBuilderSubject` through the same `derivePreviewOverlays` the action
 * center uses — and is published here through `CrmPreviewProvider`, exactly
 * as the action center publishes its own. The surface reads the subject's
 * overlay, projects it onto the saved record (or synthesises one), and
 * renders changed rows `old → new`. The floating `SaveBar` is the surface's
 * proposal-backed producer (`draftCommit`): Save approves the proposal.
 *
 * ## Creating something that does not exist yet
 *
 * Before approval there is no CRM row. A `create` overlay is what tells the
 * surface so: it renders a synthetic record, never fetches, and switches off
 * every persistence-dependent section. Before the agent has proposed
 * anything there is not even an id — so this host publishes a PLACEHOLDER
 * create overlay for the declared type, which lets the surface show the
 * type's shape instead of an apology.
 */

import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { isValidEntityTypeName } from '@/lib/crm/core/types';
import type { BuilderDraft } from '@/lib/crm/hooks/useBuilderSubject.helpers';
import { useEntityTypeSchema } from '@/lib/crm/hooks/useEntityTypeSchema';
import { CrmPreviewProvider, type PreviewOverlay } from '@/lib/crm/preview';
import {
  type DraftCommit,
  RecordSurfaceProvider,
} from '@/lib/crm/record/contexts/RecordSurfaceContext';
import { useStoreObjectSubscription } from '@/lib/crm/record/hooks/useStoreObjectSubscription';
import {
  RECORD_HEADER_ALIGN,
  RECORD_HEADER_CLASS,
} from '@/lib/crm/record/record-layout';
import {
  RecordSlideoutBody,
  RecordSlideoutFooter,
  RecordSlideoutHeaderActions,
  RecordSlideoutHeaderContent,
  RecordSlideoutHeaderIdentity,
} from '@/lib/crm/record/record-slideout-regions';
import { getEntityKindLabel } from '@/lib/crm/utils/getEntityKindLabel';

import {
  BuilderPanelFrame,
  EmptyPanel,
  ErrorPanel,
  SavingPanel,
} from './BuilderPanelStates';
import { useBuilderPanelRecordPoll } from './useBuilderPanelRecordPoll';

// Static slot elements — the regions read everything from context. Identical
// to the inline record panel's slots; the panel is the same surface.
const HEADER_SLOT = <RecordSlideoutHeaderContent />;
const HEADER_ACTIONS_SLOT = <RecordSlideoutHeaderActions />;
const HEADER_SECOND_ROW_SLOT = <RecordSlideoutHeaderIdentity />;

const EMPTY_OVERLAYS: Record<string, PreviewOverlay> = {};

/**
 * The id under which a not-yet-proposed record is projected. It is never
 * fetched: a `create` overlay disables the surface's fetch, and the sections
 * that key on an entity id are switched off while the record is unsaved.
 */
const PLACEHOLDER_ID_PREFIX = 'draft:';

interface BuilderPanelProps {
  orgId: number | undefined;
  /**
   * The type being built, declared by the conversation. Known from the moment
   * the builder opens — before any record exists and before anything is
   * proposed — which is what lets the panel show the entity's shape rather
   * than an apology.
   */
  entityTypeName: string | undefined;
  /** The entity being built. Undefined before the agent's first proposal. */
  entityId: string | undefined;
  /**
   * Overlays for every entity the pending proposal touches, keyed by entity
   * id. Published whole, so a proposal that creates the subject AND a record
   * it links to projects both. Empty when nothing is pending.
   */
  overlays: Record<string, PreviewOverlay>;
  /** The agent's latest proposed version, if it has proposed one. */
  draft: BuilderDraft | undefined;
  /**
   * True when the builder subject's proposal-detail fetch failed. A caller
   * that wires this in (rather than leaving it `undefined`) gets a distinct,
   * retryable error state instead of the panel falling through to
   * `EmptyPanel` — a real failure otherwise looks identical to "nothing
   * proposed yet."
   */
  isError?: boolean;
  /** The error behind `isError`, passed straight through to `QueryErrorState`. */
  error?: unknown;
  /** Retries the failed fetch. Passed as `QueryErrorState`'s retry action. */
  refetch?: () => void;
  /**
   * Called after Save succeeds, so the host can re-read the conversation.
   *
   * Load-bearing, not a nicety. Approving resolves the proposal, which drops
   * the draft by design — and on the create path the record's id reaches the
   * client only on the conversation, written server-side at approval. Without
   * this the host keeps the `builder_entity_id: null` it loaded with, `entityId`
   * stays undefined, and the panel falls through to `EmptyPanel` over a record
   * that exists. That was the post-Save empty-state bug.
   */
  onApproved?: () => void;
  /**
   * Called after Cancel succeeds — the draft's other resolution. The host
   * needs the same re-read as after Save: chat-built proposals get no
   * websocket update, so without it the conversation's copy of the proposal
   * keeps reading "pending" after the user discarded it.
   */
  onRejected?: () => void;
  /**
   * Dismiss the panel and return to the chat.
   *
   * Mobile only: there the panel opens full screen over the conversation, so
   * it needs its own way out. Omitted on desktop, where the panel sits beside
   * the chat and there is nothing to dismiss — passing nothing hides the
   * control rather than rendering a dead one.
   *
   * NOT the same as the header's back button, which leaves the builder.
   */
  onClosePanel?: () => void;
  /**
   * Render the chrome's close (X) button in the record header, wired to this.
   *
   * For hosts where the panel is DISMISSABLE — the ordinary chat's docked
   * proposal pane, where closing returns to the full-width conversation.
   * Builder hosts pass nothing: there the panel is the point of the page and
   * has nothing to dismiss to (desktop), or dismisses via `onClosePanel`
   * (mobile full-screen).
   */
  onClose?: () => void;
}

export function BuilderPanel({
  orgId,
  entityTypeName: declaredEntityTypeName,
  entityId,
  overlays,
  draft,
  isError,
  error,
  refetch,
  onApproved,
  onRejected,
  onClosePanel,
  onClose,
}: BuilderPanelProps) {
  const { currentOrg } = useOrg();

  const resolvedOrgId =
    orgId ??
    (typeof currentOrg?.id === 'number' ? currentOrg.id : undefined) ??
    0;

  // The RAW store entry for the subject — used for exactly two things this
  // host owns: the "record landed" signal that ends the post-Save wait, and
  // the most authoritative statement of the record's type. The surface below
  // does its own resolution; this is not passed to it.
  //
  // Deliberately NOT `useCrmObjectSnapshot`, which merges the active preview
  // overlay. This host PUBLISHES that overlay, so a merged read answers with
  // the synthetic draft from the moment the agent proposes — and the post-Save
  // wait would end before the record existed, dropping the user on "Record not
  // found" while the worker was still creating it.
  const { storeObjectData: savedObject, storeObjectIsComplete } =
    useStoreObjectSubscription(entityId ?? '');

  // The saved record is the most authoritative statement of its own type, then
  // the proposal, then the conversation's declaration — the last being the only
  // one available before anything has been proposed.
  const entityTypeNameResolved =
    savedObject?.entity_type_name ??
    draft?.overlay.entityTypeName ??
    declaredEntityTypeName;

  // The pre-proposal window on the create path: a type but no id, nothing
  // proposed, nothing saved. `entityId === undefined` is the load-bearing
  // clause — entityId is set on the edit path (pinned) and once a proposal
  // mints one — so this never overrides an existing record whose snapshot is
  // still loading.
  const placeholder = useMemo(() => {
    if (
      entityId !== undefined ||
      draft !== undefined ||
      !declaredEntityTypeName
    ) {
      return undefined;
    }
    const id = `${PLACEHOLDER_ID_PREFIX}${declaredEntityTypeName}`;
    const overlay: PreviewOverlay = {
      entityId: id,
      entityTypeName: declaredEntityTypeName,
      kind: 'create',
      properties: {},
    };
    return { id, overlays: { [id]: overlay } };
  }, [entityId, draft, declaredEntityTypeName]);

  const effectiveOverlays = draft
    ? overlays
    : (placeholder?.overlays ?? EMPTY_OVERLAYS);
  const objectId = entityId ?? draft?.overlay.entityId ?? placeholder?.id;

  // Narrow to EntityTypeName for the schema lookup — entityTypeNameResolved
  // is a plain string. isValidEntityTypeName is the established narrowing
  // pattern (see draftEntities.ts).
  const declaredTypeForSchema =
    entityTypeNameResolved && isValidEntityTypeName(entityTypeNameResolved)
      ? entityTypeNameResolved
      : undefined;
  const { schema: declaredSchema } = useEntityTypeSchema(
    resolvedOrgId,
    declaredTypeForSchema
  );
  // The concrete type is undecided ONLY on the placeholder — a builder opens
  // on "create an automation" and the agent decides scheduled vs event — so
  // the chip reads the kind, not a concrete type the proposal may change.
  const typeLabelOverride = placeholder
    ? getEntityKindLabel(declaredSchema?.entity_kind)
    : undefined;

  // On mobile the panel opens FULL SCREEN over the chat, so it needs a way to
  // dismiss back to the conversation. Deliberately NOT the same action as the
  // header's back button: that leaves the builder entirely, this only closes
  // the panel. Desktop renders the panel side-by-side and passes nothing, so
  // no dismiss control appears.
  const onBack = onClosePanel;

  // Approval is async, so "saved" is a state the panel has to sit in, not an
  // instant. We enter it when Save succeeds and leave it when the record shows
  // up in the store (websocket- or poll-fed).
  const { awaitingRecord, startAwaitingRecord, clearAwaitingRecord } =
    useBuilderPanelRecordPoll(entityId, resolvedOrgId);
  // The record landing is what ends the wait — deliberately not a timer, and
  // a previous-value compare in render rather than an effect
  // (docs/claude/REACT_19_PATTERNS.md § 4.2). Gated on the store entry being
  // COMPLETE, which only a real server response produces.
  //
  // KNOWN GAP: if execution genuinely fails, this sits on "Saving…" rather
  // than saying so. The honest signal is the proposal's `execution_status`
  // endpoint (resolution + execution_result), which this panel does not read
  // yet. Still strictly better than the bug it replaces, which showed an empty
  // panel over a created record on the HAPPY path.
  if (awaitingRecord && storeObjectIsComplete) {
    clearAwaitingRecord();
  }

  const handleApproved = useCallback(() => {
    startAwaitingRecord();
    onApproved?.();
  }, [onApproved, startAwaitingRecord]);

  // `onRejected` depends directly on the parent prop rather than a local
  // wrapper — safe only because it is expected to be a stable, parent-memoized
  // callback (currently `reloadConversationAfterResolution` in
  // ReasoningChat.tsx). An unmemoized caller would rebuild this memo — and the
  // RecordSurfaceProvider it feeds — on every render.
  const draftCommit = useMemo<DraftCommit>(
    () => ({
      proposalId: draft?.proposalId,
      onApproved: handleApproved,
      // No local wrapper: unlike Save, Cancel creates nothing to wait for.
      onRejected,
    }),
    [draft?.proposalId, handleApproved, onRejected]
  );

  // Order matters: the saving state is only reachable after a save, and it
  // outranks both the error and empty states — which must never show over a
  // record the user has already created. The error state comes next: a fetch
  // failure must never be mistaken for "nothing proposed yet," which is what
  // falling through to the empty state below would look like.
  // The frame (BuilderPanelFrame) renders the way out in EVERY state below;
  // the record header carries its own back chevron once a record shows, so
  // only the pre-record states ask the frame to draw one.
  if (awaitingRecord) {
    return (
      <BuilderPanelFrame onBack={onBack} onClose={onClose} showBack>
        <SavingPanel />
      </BuilderPanelFrame>
    );
  }

  if (isError) {
    return (
      <BuilderPanelFrame onBack={onBack} onClose={onClose} showBack>
        <ErrorPanel error={error} onRetry={refetch} />
      </BuilderPanelFrame>
    );
  }

  if (!objectId) {
    return (
      <BuilderPanelFrame onBack={onBack} onClose={onClose} showBack>
        <EmptyPanel />
      </BuilderPanelFrame>
    );
  }

  return (
    <CrmPreviewProvider overlays={effectiveOverlays}>
      {/* Keyed on the id so the surface remounts as the subject changes
          identity — placeholder to minted id — and never carries one record's
          resolution into another's. The minted id survives approval, so the
          draft-to-saved transition does NOT remount. */}
      <RecordSurfaceProvider
        key={objectId}
        objectId={objectId}
        draftCommit={draftCommit}
        // The panel is a REVIEW surface. Its header actions would be either
        // noise (Add note) or circular ("Edit with Ahoy" from inside the
        // Ahoy editor); the only action that belongs here is Save, which the
        // SaveBar owns.
        hideActions
        typeLabelOverride={typeLabelOverride}
      >
        <BuilderPanelFrame onBack={onBack} onClose={onClose} showBack={false}>
          {/* The same header row the slideout uses. The chrome's close only
              renders for hosts that passed `onClose` (dismissable docked
              pane); builder hosts keep the close-less header — beside the
              chat on desktop there is nothing to dismiss, and on mobile the
              record's own back chevron (from the frame's context) is the way
              out. */}
          <SlideoutMenu.Header
            showCloseButton={onClose !== undefined}
            align={RECORD_HEADER_ALIGN}
            className={RECORD_HEADER_CLASS}
            actions={HEADER_ACTIONS_SLOT}
            secondRow={HEADER_SECOND_ROW_SLOT}
          >
            {HEADER_SLOT}
          </SlideoutMenu.Header>
          <SlideoutMenu.Content className='px-0 md:px-0'>
            <RecordSlideoutBody />
          </SlideoutMenu.Content>
          {/* The chrome's pinned footer slot: the same self-gating footers the
              slideout renders (Run now for a saved scheduled automation with
              nothing pending, and so on). */}
          <RecordSlideoutFooter />
        </BuilderPanelFrame>
      </RecordSurfaceProvider>
    </CrmPreviewProvider>
  );
}
